/**
 * AccountChoiceDialog (Jul-2026, User-Wunsch) — nach dem Login fragen:
 * „Als Händler oder privat fortfahren?" wenn verknüpfte Konten existieren.
 * Merkt sich das zuletzt genutzte Konto (localStorage) und hebt es hervor.
 */
import { useState } from "react";
import { Loader2, Building2, User, Wrench, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export const rememberAccount = (id) => { try { localStorage.setItem("tw_last_account_id", id || ""); } catch { /* ignore */ } };
export const lastAccountId = () => { try { return localStorage.getItem("tw_last_account_id") || ""; } catch { return ""; } };
// Jul-2026 (User-Wunsch) — „Diese Wahl merken und nicht mehr fragen".
export const autoChoiceEnabled = () => { try { return localStorage.getItem("tw_account_choice_auto") === "1"; } catch { return false; } };
export const setAutoChoice = (on) => { try { localStorage.setItem("tw_account_choice_auto", on ? "1" : ""); } catch { /* ignore */ } };

const ROLE_META = {
    b2b: { label: "Händler", Icon: Building2 },
    zweiradverkaeufer: { label: "Mitarbeiter", Icon: Wrench },
    vermittler: { label: "Vermittler / Verkäufer", Icon: User },
    b2c: { label: "Privat", Icon: User },
    service_provider: { label: "Dienstleister", Icon: Wrench },
};

export const routeForRole = (role, user = null) => {
    // Aug-2026 (User-Wunsch): Kundendienst startet immer im Werkstattkalender.
    if (String(user?.employee_function || "").toLowerCase().includes("kundendienst")) return "/dealer/workshop-calendar";
    return role === "vermittler" ? "/vermittler"
        : role === "service_provider" ? "/dienstleister/dashboard"
        : role === "admin" ? "/admin/dealers" : "/dashboard";
};

export default function AccountChoiceDialog({ currentUser, accounts, hideSelf = false, onContinue }) {
    const { setSession } = useAuth();
    const [busyId, setBusyId] = useState(null);
    const [remember, setRemember] = useState(false);
    const last = lastAccountId();

    const stay = () => {
        rememberAccount(currentUser?.id);
        if (remember) setAutoChoice(true);
        onContinue();
    };

    const switchTo = async (acc) => {
        setBusyId(acc.id);
        try {
            const { data } = await api.post("/auth/switch-account", { target_user_id: acc.id });
            setSession(data.user, data.token);
            rememberAccount(acc.id);
            if (remember) setAutoChoice(true);
            toast.success(`Angemeldet als ${data.user?.name || acc.name}`);
            window.location.assign(routeForRole(data.user?.role, data.user));
        } catch {
            setBusyId(null);
            toast.error("Kontowechsel fehlgeschlagen — du bleibst im aktuellen Konto.");
            stay();
        }
    };

    // Jul-2026 (User-Wunsch) — Filialleiter: die eigene Mitarbeiter-Karte nicht
    // anbieten, Auswahl = Händler-Login oder Vermittler-/Verkäufer-Login.
    // Jul-2026 (Nadja-Fall) — das eigene Konto darf niemals doppelt erscheinen:
    // Duplikate nach Konto-ID herausfiltern (Defense-in-Depth zum Backend-Fix).
    const raw = hideSelf ? [...accounts] : [
        { ...currentUser, _current: true },
        ...accounts,
    ];
    const seenIds = new Set();
    const options = raw.filter((a) => {
        if (!a?.id || seenIds.has(a.id)) return false;
        seenIds.add(a.id);
        return true;
    });

    return (
        <Dialog open onOpenChange={(o) => !o && stay()}>
            <DialogContent className="max-w-md" data-testid="account-choice-dialog">
                <DialogHeader>
                    <DialogTitle>Wie möchtest du fortfahren?</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">Mit deinem Login sind mehrere Konten verknüpft — wähle, in welchem Kontext du arbeiten willst.</p>
                <div className="space-y-2">
                    {options.map((acc) => {
                        const meta = ROLE_META[acc.role] || { label: acc.role, Icon: User };
                        const Icon = meta.Icon;
                        return (
                            <button
                                key={acc.id}
                                type="button"
                                disabled={busyId !== null}
                                onClick={() => (acc._current ? stay() : switchTo(acc))}
                                className="w-full flex items-center gap-3 border-2 border-gray-200 hover:border-[#FFCC00] rounded-sm p-3 text-left transition-colors disabled:opacity-60"
                                data-testid={`account-choice-${acc.id}`}
                            >
                                <span className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${acc.role === "b2b" ? "bg-[#111827] text-[#FFCC00]" : "bg-[#FFCC00] text-[#111827]"}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-2 flex-wrap">
                                        <b className="text-sm truncate">{acc.company_name || acc.name || acc.email}</b>
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 rounded-sm px-1.5 py-0.5">{meta.label}</span>
                                        {acc.employer_name && <span className="text-[10px] text-gray-400 truncate">· {acc.employer_name}</span>}
                                        {last && last === acc.id && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] rounded-sm px-1.5 py-0.5" data-testid={`account-choice-last-${acc.id}`}>Zuletzt genutzt</span>
                                        )}
                                    </span>
                                    {acc.email && !acc.email.endsWith("@filiale.local") && (
                                        <span className="block text-[11px] text-gray-400 truncate">{acc.email}</span>
                                    )}
                                </span>
                                {busyId === acc.id ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />}
                            </button>
                        );
                    })}
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none" data-testid="account-choice-remember">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="account-choice-remember-checkbox" />
                    Diese Wahl merken und nicht mehr fragen
                    <span className="text-[10px] text-gray-400">(zurücksetzen im Kopfmenü → „Konto wechseln“)</span>
                </label>
            </DialogContent>
        </Dialog>
    );
}
